import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import { useAuth } from "@/context/AuthContext";
import { getCategories } from "@/services/publicApi";
import {
  createAdminCategory,
  deleteAdminCategory,
  updateAdminCategory,
  type CategoryInput,
} from "@/services/adminApi";
import { categoryGroups } from "@/mocks/categories";
import type { Category } from "@/types";

const slugify = (value: string) =>
  value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

interface DraftState {
  name: string;
  group: string;
  description: string;
}

const emptyDraft: DraftState = { name: "", group: categoryGroups[0], description: "" };

export default function AdminCategories() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getCategories()
      .then((c) => active && setRows(c))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2400);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter(
      (r) => !q || r.name.toLowerCase().includes(q) || (r.group ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) return;
    const payload: CategoryInput = {
      name,
      slug: slugify(name),
      group: draft.group || undefined,
      description: draft.description.trim() || undefined,
    };
    setSaving(true);

    if (editingId) {
      const ok = await updateAdminCategory(editingId, payload, token);
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that category. Please try again.");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...payload } : r)));
      flash(`Category “${name}” updated.`);
    } else {
      const created = await createAdminCategory(payload, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that category. Please try again.");
        return;
      }
      setRows((prev) => [...prev, created]);
      flash(`Category “${name}” created.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: Category) => {
    setEditingId(row.id);
    setDraft({ name: row.name, group: row.group ?? categoryGroups[0], description: row.description });
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (row: Category) => {
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminCategory(row.id, token);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that category. Please try again.");
      return;
    }
    if (editingId === row.id) resetForm();
    flash("Category removed.");
  };

  const columns: DataColumn<Category>[] = [
    {
      key: "name",
      header: "Category",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className={r.icon || "ri-price-tag-3-line"}></i>
          </span>
          <div>
            <p className="font-semibold text-foreground-950">{r.name}</p>
            <p className="text-xs text-foreground-500">/{r.slug}</p>
          </div>
        </div>
      ),
    },
    { key: "group", header: "Group", render: (r) => <span className="text-foreground-700">{r.group ?? "—"}</span>, nowrap: true },
    { key: "description", header: "Description", render: (r) => <span className="text-foreground-500">{r.description}</span> },
    { key: "businessCount", header: "Listings", align: "right", render: (r) => <span className="font-semibold text-foreground-900">{r.businessCount.toLocaleString()}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit category"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete category"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Categories" path="/admin/categories" />

      <AdminPageHeader
        icon="ri-apps-2-line"
        title="Categories"
        subtitle="Manage the top-level categories that organise every listing."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New category"}
            </button>
          </>
        }
      />

      {showForm && (
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label htmlFor="catName" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Name</label>
            <input
              id="catName"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Pet Grooming"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="catDesc" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Description</label>
            <input
              id="catDesc"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="Short summary"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div className="lg:w-52">
            <label htmlFor="catGroup" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Group</label>
            <select
              id="catGroup"
              value={draft.group}
              onChange={(e) => setDraft((d) => ({ ...d, group: e.target.value }))}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              {categoryGroups.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={saving || !draft.name.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {editingId ? "Save changes" : "Save"}
          </button>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search categories or groups…" />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load categories. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="820px" />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this category?"
        message={pendingDelete ? `“${pendingDelete.name}” will be removed. Listings in this category may become uncategorised. This cannot be undone.` : ""}
        confirmLabel="Delete category"
        busy={deleting}
        onConfirm={async () => {
          const target = pendingDelete;
          if (!target) return;
          setDeleting(true);
          await remove(target);
          setDeleting(false);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}